import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useStore } from '../../contexts/StoreContext';
import { questionnairesApi, type ActiveQuestionnaire, type ActiveQuestionnaireQuestion } from '../../api/endpoints/questionnaires';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Textarea';
import { QIcon } from './questionnaireIcons';

// Track which questionnaires we've shown this session so we don't re-pop them
// after a query refetch (the backend is the source of truth, but during the
// gap between submit and refetch we'd briefly show it again).
const shownThisSession = new Set<string>();

export function QuestionnaireModal() {
  const { storeId } = useParams();
  const { user, isDemo } = useAuth();
  const { currentStore } = useStore();
  const qc = useQueryClient();

  // Skip the network call for non-owners (employees). Backend re-checks via
  // store.createdBy, but the role flag avoids a useless request for every
  // team member on every dashboard load. Demo mode is also skipped.
  const isOwner = currentStore?.role === 'owner';
  const enabled = !!storeId && !!user && !isDemo && isOwner;

  const { data } = useQuery({
    queryKey: ['questionnaires', 'active', storeId],
    queryFn: () => questionnairesApi.active(storeId!),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const queue = useMemo(
    () => (data || []).filter((q) => !shownThisSession.has(q._id)),
    [data],
  );
  const current = queue[0];

  if (!current) return null;
  return (
    <SingleQuestionnaire
      key={current._id}
      storeId={storeId!}
      questionnaire={current}
      onDone={() => {
        shownThisSession.add(current._id);
        qc.invalidateQueries({ queryKey: ['questionnaires', 'active', storeId] });
      }}
    />
  );
}

function SingleQuestionnaire({
  storeId,
  questionnaire,
  onDone,
}: {
  storeId: string;
  questionnaire: ActiveQuestionnaire;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(true);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});

  const close = () => {
    setOpen(false);
    onDone();
  };

  const respond = useMutation({
    mutationFn: () => questionnairesApi.respond(storeId, questionnaire._id, answers),
    onSuccess: () => close(),
  });

  const dismiss = useMutation({
    mutationFn: () => questionnairesApi.dismiss(storeId, questionnaire._id),
    onSuccess: () => close(),
  });

  const missingRequired = questionnaire.questions.some((q) => {
    if (!q.required) return false;
    const v = answers[q.id];
    if (q.type === 'multi') return !Array.isArray(v) || v.length === 0;
    return v === undefined || v === null || v === '';
  });

  return (
    <Modal
      isOpen={open}
      onClose={() => dismiss.mutate()}
      title={questionnaire.title}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => dismiss.mutate()} isLoading={dismiss.isPending}>
            ليس الآن
          </Button>
          <Button
            variant="primary"
            onClick={() => respond.mutate()}
            disabled={missingRequired}
            isLoading={respond.isPending}
          >
            إرسال
          </Button>
        </>
      }
    >
      <div className="space-y-5" dir="rtl">
        {questionnaire.description ? (
          <p className="text-sm text-text-secondary leading-relaxed">{questionnaire.description}</p>
        ) : null}
        {questionnaire.questions.map((q) => (
          <QuestionField
            key={q.id}
            question={q}
            value={answers[q.id]}
            onChange={(value) => setAnswers((prev) => ({ ...prev, [q.id]: value }))}
          />
        ))}
      </div>
    </Modal>
  );
}

function QuestionField({
  question,
  value,
  onChange,
}: {
  question: ActiveQuestionnaireQuestion;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {question.icon ? <QIcon name={question.icon} size={16} className="text-text-secondary" /> : null}
        <label className="text-sm font-medium text-text-primary">
          {question.text}
          {question.required ? <span className="text-danger ms-1">*</span> : null}
        </label>
      </div>
      {question.type === 'text' ? (
        <Textarea
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder="اكتب إجابتك هنا..."
        />
      ) : null}

      {question.type === 'single' ? (
        <div className="flex flex-col gap-1.5">
          {(question.options || []).map((opt, i) => (
            <label
              key={i}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border-default bg-bg-field hover:border-border-hover cursor-pointer"
            >
              <input
                type="radio"
                name={`q-${question.id}`}
                checked={value === opt}
                onChange={() => onChange(opt)}
                className="accent-accent-action"
              />
              <span className="text-sm text-text-primary">{opt}</span>
            </label>
          ))}
        </div>
      ) : null}

      {question.type === 'multi' ? (
        <div className="flex flex-col gap-1.5">
          {(question.options || []).map((opt, i) => {
            const arr = Array.isArray(value) ? (value as string[]) : [];
            const checked = arr.includes(opt);
            return (
              <label
                key={i}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border-default bg-bg-field hover:border-border-hover cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...arr, opt]);
                    else onChange(arr.filter((x) => x !== opt));
                  }}
                  className="accent-accent-action"
                />
                <span className="text-sm text-text-primary">{opt}</span>
              </label>
            );
          })}
        </div>
      ) : null}

      {question.type === 'rating' ? (
        <RatingScale
          min={question.min ?? 1}
          max={question.max ?? 10}
          value={typeof value === 'number' ? value : null}
          onChange={(v) => onChange(v)}
        />
      ) : null}
    </div>
  );
}

function RatingScale({
  min,
  max,
  value,
  onChange,
}: {
  min: number;
  max: number;
  value: number | null;
  onChange: (v: number) => void;
}) {
  const cells: number[] = [];
  for (let i = min; i <= max; i++) cells.push(i);
  return (
    <div className="flex flex-wrap gap-1.5">
      {cells.map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={
              'min-w-[40px] h-10 px-2 rounded-lg border text-sm font-semibold transition-colors ' +
              (active
                ? 'bg-accent-action text-white border-accent-action'
                : 'bg-bg-field text-text-primary border-border-default hover:border-border-hover')
            }
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

